import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	FormControl,
	Grid,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { Download, Search } from "lucide-react";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import { useAuth } from "../../contexts/AuthContext";
import { useSelectedAcademicYear } from "../../hooks/useSelectedAcademicYear";
import AcademicYearSelector from "../../components/common/AcademicYearSelector";
import { ROLES } from "../../config/supabase";
import { getAcademicYearRange } from "../../utils/academicYear";
import { formatServiceDate } from "../../utils/serviceWindow";
import {
	VISIT_COMPLIANCE_STATUS,
	buildFmePerformanceMetrics,
	filterMetricsRows,
	serviceWindowOverlapsAcademicYear,
} from "../../utils/fmePerformanceMetrics";

const STATUS_COLORS = {
	[VISIT_COMPLIANCE_STATUS.COMPLETED]: "success",
	[VISIT_COMPLIANCE_STATUS.EARLY]: "info",
	[VISIT_COMPLIANCE_STATUS.LATE]: "warning",
	[VISIT_COMPLIANCE_STATUS.MISSED]: "error",
	[VISIT_COMPLIANCE_STATUS.PENDING]: "default",
	[VISIT_COMPLIANCE_STATUS.UPCOMING]: "default",
	[VISIT_COMPLIANCE_STATUS.NO_WINDOW]: "default",
};

const FmePerformanceMetrics = () => {
	const { supabase } = useAuth();
	const { selectedAcademicYear, setSelectedAcademicYear } =
		useSelectedAcademicYear();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [metrics, setMetrics] = useState({ rows: [], summaries: [], totals: {} });
	const [serviceWindows, setServiceWindows] = useState([]);
	const [search, setSearch] = useState("");
	const [selectedFme, setSelectedFme] = useState("all");
	const [selectedVisitType, setSelectedVisitType] = useState("all");
	const [selectedStatus, setSelectedStatus] = useState("all");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(25);

	const fetchReportData = useCallback(async () => {
		try {
			setLoading(true);
			setError("");

			const academicYearRange = getAcademicYearRange(selectedAcademicYear);

			const [
				{ data: employees, error: employeesError },
				{ data: assignments, error: assignmentsError },
				{ data: visitTypes, error: visitTypesError },
				{ data: visits, error: visitsError },
			] = await Promise.all([
				supabase
					.from("profiles")
					.select("id, full_name, email, status")
					.eq("role", ROLES.FUNMIYO_EMPLOYEE)
					.eq("status", "Active")
					.order("full_name"),
				supabase
					.from("employee_schools")
					.select(
						`
						profile_id,
						school_id,
						schools:school_id (
							id,
							name,
							district,
							status
						)
					`
					)
					.eq("active_status", true),
				supabase
					.from("visit_types")
					.select("*")
					.eq("status", true)
					.eq("visit_type", "Service")
					.not("start_date", "is", null)
					.not("end_date", "is", null)
					.order("start_date"),
				supabase
					.from("school_visits")
					.select(
						`
						id,
						school_id,
						visit_type_id,
						visited_by,
						planned_by,
						visited_date,
						planned_date
					`
					)
					.not("visit_type_id", "is", null),
			]);

			if (employeesError) throw employeesError;
			if (assignmentsError) throw assignmentsError;
			if (visitTypesError) throw visitTypesError;
			if (visitsError) throw visitsError;

			const windowsForYear = (visitTypes || []).filter((window) =>
				serviceWindowOverlapsAcademicYear(window, academicYearRange)
			);

			const activeAssignments = (assignments || []).filter(
				(row) => row.schools && row.schools.status === "Active"
			);

			const report = buildFmePerformanceMetrics({
				employees: employees || [],
				assignments: activeAssignments,
				serviceWindows: windowsForYear,
				visits: visits || [],
			});

			setServiceWindows(windowsForYear);
			setMetrics(report);
		} catch (err) {
			console.error("Error loading FME performance metrics:", err);
			setError("Failed to load FME performance metrics.");
		} finally {
			setLoading(false);
		}
	}, [supabase, selectedAcademicYear]);

	useEffect(() => {
		if (selectedAcademicYear) {
			fetchReportData();
		}
	}, [fetchReportData, selectedAcademicYear]);

	const filteredRows = useMemo(
		() =>
			filterMetricsRows(metrics.rows, {
				search,
				fmeId: selectedFme,
				visitTypeId: selectedVisitType,
				status: selectedStatus,
			}),
		[metrics.rows, search, selectedFme, selectedVisitType, selectedStatus]
	);

	const paginatedRows = useMemo(
		() =>
			filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
		[filteredRows, page, rowsPerPage]
	);

	const handleExport = () => {
		const exportRows = filteredRows.map((row) => ({
			FME: row.fmeName,
			Email: row.fmeEmail,
			School: row.schoolName,
			District: row.schoolDistrict,
			"Service Window": row.visitTypeName,
			"Window Start": row.windowStart
				? dayjs(row.windowStart).format("DD/MM/YYYY")
				: "",
			"Window End": row.windowEnd
				? dayjs(row.windowEnd).format("DD/MM/YYYY")
				: "",
			"Visited Date": row.visitedDate
				? dayjs(row.visitedDate).format("DD/MM/YYYY")
				: "",
			Status: row.status,
		}));

		const summaryRows = metrics.summaries.map((summary) => ({
			FME: summary.fmeName,
			Email: summary.fmeEmail,
			"Assigned Schools": summary.assignedSchools,
			"Expected Visits": summary.expectedVisits,
			"Completed On Time": summary.completedOnTime,
			Early: summary.early,
			Late: summary.late,
			Missed: summary.missed,
			Pending: summary.pending,
			Upcoming: summary.upcoming,
			"Compliance %": summary.complianceRate,
		}));

		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(
			workbook,
			XLSX.utils.json_to_sheet(exportRows),
			"Visit Details"
		);
		XLSX.utils.book_append_sheet(
			workbook,
			XLSX.utils.json_to_sheet(summaryRows),
			"FME Summary"
		);
		XLSX.writeFile(
			workbook,
			`fme-performance-metrics-${selectedAcademicYear}.xlsx`
		);
	};

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
				<CircularProgress size={28} />
			</Box>
		);
	}

	return (
		<Box sx={{ p: { xs: 1, md: 2 } }}>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: { xs: "flex-start", md: "center" },
					flexDirection: { xs: "column", md: "row" },
					gap: 2,
					mb: 3,
				}}>
				<Box>
					<Typography variant="h5" fontWeight={700}>
						FME Performance Metrics
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						Tracks whether Funmath Experts complete required service window visits
						on assigned schools within each visit type window.
					</Typography>
				</Box>
				<Stack direction="row" spacing={1.5} alignItems="center">
					<AcademicYearSelector
						value={selectedAcademicYear}
						onChange={setSelectedAcademicYear}
					/>
					<Button
						variant="outlined"
						startIcon={<Download size={16} />}
						onClick={handleExport}
						disabled={filteredRows.length === 0}>
						Export
					</Button>
				</Stack>
			</Box>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{serviceWindows.length === 0 && !error && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					No service windows with start and end dates are configured for{" "}
					{selectedAcademicYear}. Add dates in Master Data → Visit Types.
				</Alert>
			)}

			<Grid container spacing={2} sx={{ mb: 3 }}>
				<Grid item xs={12} sm={6} md={3}>
					<Card variant="outlined">
						<CardContent>
							<Typography variant="caption" color="text.secondary">
								FMEs tracked
							</Typography>
							<Typography variant="h4">{metrics.totals.fmeCount || 0}</Typography>
						</CardContent>
					</Card>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<Card variant="outlined">
						<CardContent>
							<Typography variant="caption" color="text.secondary">
								Overall compliance
							</Typography>
							<Typography variant="h4">
								{metrics.totals.complianceRate || 0}%
							</Typography>
						</CardContent>
					</Card>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<Card variant="outlined">
						<CardContent>
							<Typography variant="caption" color="text.secondary">
								Missed visits
							</Typography>
							<Typography variant="h4" color="error.main">
								{metrics.totals.missed || 0}
							</Typography>
						</CardContent>
					</Card>
				</Grid>
				<Grid item xs={12} sm={6} md={3}>
					<Card variant="outlined">
						<CardContent>
							<Typography variant="caption" color="text.secondary">
								Pending / upcoming
							</Typography>
							<Typography variant="h4">
								{(metrics.totals.pending || 0) + (metrics.totals.upcoming || 0)}
							</Typography>
						</CardContent>
					</Card>
				</Grid>
			</Grid>

			<Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
				<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
					FME summary
				</Typography>
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>FME</TableCell>
								<TableCell>Schools</TableCell>
								<TableCell align="right">Expected</TableCell>
								<TableCell align="right">On time</TableCell>
								<TableCell align="right">Late</TableCell>
								<TableCell align="right">Missed</TableCell>
								<TableCell align="right">Pending</TableCell>
								<TableCell align="right">Compliance</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{metrics.summaries.length === 0 ? (
								<TableRow>
									<TableCell colSpan={8} align="center">
										No FME performance data for the selected academic year.
									</TableCell>
								</TableRow>
							) : (
								metrics.summaries.map((summary) => (
									<TableRow key={summary.fmeId} hover>
										<TableCell>
											<Typography variant="body2" fontWeight={600}>
												{summary.fmeName}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{summary.fmeEmail}
											</Typography>
										</TableCell>
										<TableCell>{summary.assignedSchools}</TableCell>
										<TableCell align="right">{summary.expectedVisits}</TableCell>
										<TableCell align="right">{summary.completedOnTime}</TableCell>
										<TableCell align="right">{summary.late}</TableCell>
										<TableCell align="right">{summary.missed}</TableCell>
										<TableCell align="right">{summary.pending}</TableCell>
										<TableCell align="right">
											<Chip
												size="small"
												label={`${summary.complianceRate}%`}
												color={
													summary.complianceRate >= 80
														? "success"
														: summary.complianceRate >= 50
														? "warning"
														: "error"
												}
											/>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>

			<Paper variant="outlined" sx={{ p: 2 }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: { xs: "stretch", md: "center" },
						flexDirection: { xs: "column", md: "row" },
						gap: 2,
						mb: 2,
					}}>
					<Typography variant="subtitle1" fontWeight={600}>
						School visit compliance details
					</Typography>
					<Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
						<TextField
							size="small"
							placeholder="Search FME, school, visit type..."
							value={search}
							onChange={(e) => {
								setSearch(e.target.value);
								setPage(0);
							}}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Search size={16} />
									</InputAdornment>
								),
							}}
							sx={{ minWidth: 260 }}
						/>
						<FormControl size="small" sx={{ minWidth: 180 }}>
							<InputLabel>FME</InputLabel>
							<Select
								value={selectedFme}
								label="FME"
								onChange={(e) => {
									setSelectedFme(e.target.value);
									setPage(0);
								}}>
								<MenuItem value="all">All FMEs</MenuItem>
								{metrics.summaries.map((summary) => (
									<MenuItem key={summary.fmeId} value={summary.fmeId}>
										{summary.fmeName}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl size="small" sx={{ minWidth: 180 }}>
							<InputLabel>Service window</InputLabel>
							<Select
								value={selectedVisitType}
								label="Service window"
								onChange={(e) => {
									setSelectedVisitType(e.target.value);
									setPage(0);
								}}>
								<MenuItem value="all">All windows</MenuItem>
								{serviceWindows.map((window) => (
									<MenuItem key={window.id} value={window.id}>
										{window.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControl size="small" sx={{ minWidth: 160 }}>
							<InputLabel>Status</InputLabel>
							<Select
								value={selectedStatus}
								label="Status"
								onChange={(e) => {
									setSelectedStatus(e.target.value);
									setPage(0);
								}}>
								<MenuItem value="all">All statuses</MenuItem>
								{Object.values(VISIT_COMPLIANCE_STATUS).map((status) => (
									<MenuItem key={status} value={status}>
										{status}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Stack>
				</Box>

				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>FME</TableCell>
								<TableCell>School</TableCell>
								<TableCell>Service window</TableCell>
								<TableCell>Window</TableCell>
								<TableCell>Visited date</TableCell>
								<TableCell>Status</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{paginatedRows.length === 0 ? (
								<TableRow>
									<TableCell colSpan={6} align="center">
										No matching visit records.
									</TableCell>
								</TableRow>
							) : (
								paginatedRows.map((row) => (
									<TableRow key={row.id} hover>
										<TableCell>{row.fmeName}</TableCell>
										<TableCell>
											<Typography variant="body2">{row.schoolName}</Typography>
											<Typography variant="caption" color="text.secondary">
												{row.schoolDistrict || "—"}
											</Typography>
										</TableCell>
										<TableCell>{row.visitTypeName}</TableCell>
										<TableCell>
											{formatServiceDate(row.windowStart)} –{" "}
											{formatServiceDate(row.windowEnd)}
										</TableCell>
										<TableCell>
											{row.visitedDate
												? dayjs(row.visitedDate).format("DD MMM YYYY")
												: "—"}
										</TableCell>
										<TableCell>
											<Chip
												size="small"
												label={row.status}
												color={STATUS_COLORS[row.status] || "default"}
											/>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>

				<TablePagination
					component="div"
					count={filteredRows.length}
					page={page}
					onPageChange={(_, newPage) => setPage(newPage)}
					rowsPerPage={rowsPerPage}
					onRowsPerPageChange={(event) => {
						setRowsPerPage(parseInt(event.target.value, 10));
						setPage(0);
					}}
					rowsPerPageOptions={[10, 25, 50, 100]}
				/>
			</Paper>
		</Box>
	);
};

export default FmePerformanceMetrics;
